import React, { useEffect, useState, useRef, useCallback } from 'react';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';
import { registerAllModules } from 'handsontable/registry';
import template from '../assets/dailyReportTemplate.json';
import * as XLSX from 'xlsx';
import RouteHistorySidebar from './RouteHistorySidebar';
import { socket } from '../socket';
import { debounce } from 'lodash';
import { auth } from '../firebaseConfig';
import { formatAmountDisplay, safeAmountToNumber, processAmountInput } from '../utils/numberUtils';
import VehicleHistorySidebar from './VehicleHistorySidebar';
import FullScreenButton from './FullScreenButton';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouteIcon from '@mui/icons-material/Route';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GroupsIcon from '@mui/icons-material/Groups';
import NoteIcon from '@mui/icons-material/Note';
import EditIcon from '@mui/icons-material/Edit';

registerAllModules();

// 청구운송비(빨간색) 렌더러 등록
try {
  (Handsontable.renderers as any).registerRenderer('RedMoneyRenderer', function(
    instance: any, td: any, row: any, col: any, prop: any, value: any, cellProperties: any
  ) {
    Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
    td.style.color = '#d32f2f';
    td.style.fontWeight = '700';
    td.style.fontSize = '15px';
    td.style.textAlign = 'right';
  });
} catch (e) {}
// 지불운송비(파란색) 렌더러 등록
try {
  (Handsontable.renderers as any).registerRenderer('BlueMoneyRenderer', function(
    instance: any, td: any, row: any, col: any, prop: any, value: any, cellProperties: any
  ) {
    Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
    td.style.color = '#1976d2';
    td.style.fontWeight = '700';
    td.style.fontSize = '15px';
    td.style.textAlign = 'right';
  });
} catch (e) {}

const COLUMN_HEADERS = [
  '날짜', '거래처상호', '차량번호', '운송구간', '품명', '차종', '청구운송비', '소속', '연락처', '지불운송비', '손익', '사업자번호', '이름', '거리', '중량', '비고'
];
const COLUMN_FIELDS = [
  'date', 'company', 'vehicleNumber', 'route', 'itemName', 'cargo', 'billingAmount', 'department', 'contact', 'paymentAmount', 'profit', 'businessNumber', 'driverName', 'distance', 'weight', 'remarks'
];
const colWidths = [100, 150, 120, 190, 90, 60, 90, 100, 120, 90, 90, 120, 100, 80, 80, 180];

// formatNumberComma 함수를 공통 함수로 대체

function parseNumber(val: any) {
  if (val === undefined || val === null || val === '') return 0;
  return Number(String(val).replace(/[^\d.-]/g, '')) || 0;
}

function getDateList(start: string, end: string) {
  const result: string[] = [];
  let cur = new Date(start);
  const endDate = new Date(end);
  while (cur <= endDate) {
    result.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return result;
}

// Firestore 문서ID 금지문자 치환 함수(백엔드와 동일하게)
function safeFirestoreId(str: string) {
  return (str || '').replace(/[\/.#$\[\]]/g, '_');
}
function makeRouteKey(company: string, route: string, cargo: string) {
  return safeFirestoreId(`${company}|${route}|${cargo}`);
}

// 운송구간 이력/통계 캐시 타입
interface RouteHistoryCache {
  recentHistory: any[];
  avg: number | null;
  mode: number | null;
  min: number | null;
  max: number | null;
  count: number | null;
  lastUsed: string | null;
}

// 상단 필터 타입 명시
interface Filters {
  company: string[];      // 다중 선택으로 변경
  date: string[];         // 다중 선택으로 변경
  vehicleNumber: string;
  route: string;
  itemName: string[];     // 다중 선택으로 변경
  cargo: string[];        // 다중 선택으로 변경
  department: string[];   // 다중 선택으로 변경
  remarks: string;        // 비고 필터는 텍스트 입력 유지
}

// vehicleNumber, route 둘 다 없으면 무조건 빈행으로 간주
function isEmptyRow(row: any) {
  return !row.vehicleNumber && !row.route;
}

const IntegratedReportPage: React.FC<{ isFullScreen: boolean; setIsFullScreen: (v: boolean) => void; }> = ({ isFullScreen, setIsFullScreen }) => {
  const [loading, setLoading] = useState(false);
  const [allRows, setAllRows] = useState<any[]>([]); // 모든 날짜의 row 통합
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(() => {
    const today = new Date();
    return {
      start: today.toISOString().slice(0, 10),
      end: today.toISOString().slice(0, 10)
    };
  });
  const [pendingDateRange, setPendingDateRange] = useState<{ start: string; end: string }>(() => {
    const today = new Date();
    return {
      start: today.toISOString().slice(0, 10),
      end: today.toISOString().slice(0, 10)
    };
  });
  const [filters, setFilters] = useState<Filters>({ 
    company: [], 
    date: [], 
    vehicleNumber: '', 
    route: '', 
    itemName: [], 
    cargo: [],
    department: [],  // 소속 필터 초기값 추가
    remarks: ''      // 비고 필터 초기값 추가
  });

  // 필터 팝업 상태 관리
  const [filterPopups, setFilterPopups] = useState({
    company: false,
    date: false,
    itemName: false,
    cargo: false,
    department: false
  });

  // 임시 선택 상태 (팝업에서 사용)
  const [tempSelections, setTempSelections] = useState({
    company: [] as string[],
    date: [] as string[],
    itemName: [] as string[],
    cargo: [] as string[],
    department: [] as string[]
  });

  // 필터 팝업 검색어 상태
  const [filterSearchTerms, setFilterSearchTerms] = useState({
    company: '',
    date: '',
    itemName: '',
    cargo: '',
    department: ''
  });
  const [dirty, setDirty] = useState(false);
  const hotRef = useRef<any>(null);
  const [routeHistoryOpen, setRouteHistoryOpen] = useState(false);
  const [routeHistoryData, setRouteHistoryData] = useState<RouteHistoryCache>({
    recentHistory: [],
    avg: null,
    mode: null,
    min: null,
    max: null,
    count: null,
    lastUsed: null,
  });
  const [routeHistoryLoading, setRouteHistoryLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ [rowId: string]: { status: 'success' | 'error' | 'saving' | null, message?: string } }>({});
  const [hasRestored, setHasRestored] = useState(false);
  const lastEventIdRef = useRef<string | null>(null);
  const lastFetchId = useRef(0);
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [distributeAmount, setDistributeAmount] = useState('');
  const [distributeLoading, setDistributeLoading] = useState(false);
  const [routeHistoryOffset, setRouteHistoryOffset] = useState(0);
  const [routeHistoryKey, setRouteHistoryKey] = useState<string | null>(null);
  const [vehicleHistoryOpen, setVehicleHistoryOpen] = useState(false);
  const [vehicleHistoryData, setVehicleHistoryData] = useState<any>({
    recentHistory: [],
    avg: null,
    mode: null,
    min: null,
    max: null,
    count: null,
    lastUsed: null,
  });
  const [vehicleHistoryLoading, setVehicleHistoryLoading] = useState(false);
  const [vehicleHistoryKey, setVehicleHistoryKey] = useState<string | null>(null);
  const [vehicleHistoryVehicleNumber, setVehicleHistoryVehicleNumber] = useState('');
  const [highlightedRowIds, setHighlightedRowIds] = useState<Set<string>>(new Set());
  const [editMode, setEditMode] = useState<'edit' | 'fare'>('fare');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // 디바운스된 fetch 함수 추가
  const debouncedFetch = useCallback(
    debounce(async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/integrated-reports?start=${dateRange.start}&end=${dateRange.end}`);
        const rows = await res.json();
        setAllRows(rows);
      } catch (error) {
        console.error('데이터 fetch 실패:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
    [dateRange]
  );

  // 날짜 범위가 변경될 때마다 데이터를 fetch로 불러오기 (항상 서버 fetch)
  useEffect(() => {
    debouncedFetch();
  }, [dateRange, debouncedFetch]);

  // 행 추가/삭제 등 구조 변경 시: 전체 재조회 대신 증분 이벤트(report-updated row_added/row_deleted)만 사용
  useEffect(() => {
    const handleStructureUpdated = (data: { date: string }) => {
      // no-op: row_added/row_deleted 이벤트에서 증분 반영
      if (!data.date || data.date < dateRange.start || data.date > dateRange.end) return;
    };
    socket.on('dispatch-row-structure-updated', handleStructureUpdated);
    return () => {
      socket.off('dispatch-row-structure-updated', handleStructureUpdated);
    };
  }, [dateRange]);

  // 4) 포커스/가시성/소켓 재연결 시 강제 재조회 비활성화 (대용량 편집 세션 보호)
  useEffect(() => {
    // intentionally disabled: 실시간 동기화는 소켓 증분 이벤트로 유지
  }, [dateRange, debouncedFetch]);

  // fetchRows 함수 등에서 실패 시 setAllRows([])로 초기화
  const fetchRows = async (start: string, end: string) => {
    setLoading(true);
    const currentId = ++lastFetchId.current;
    try {
      const res = await fetch(`/api/integrated-reports?start=${start}&end=${end}`);
      if (!res.ok) throw new Error('데이터 fetch 실패');
      const rows = await res.json();
      if (currentId !== lastFetchId.current) return;
      setAllRows(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setAllRows([]);
      setLoading(false);
    } finally {
      if (currentId === lastFetchId.current) setLoading(false);
    }
  };

  // allRows, filteredRows, sortedRows, tableData 등에서 undefined/null 방어
  const safeAllRows = Array.isArray(allRows) ? allRows : [];
  const filteredRows = safeAllRows.filter(row => {
    if (!row) return false;
    if (isEmptyRow(row)) return false;
    
    // 거래처 필터 (다중 선택)
    if (filters.company.length > 0 && !filters.company.some(company => row.company?.includes(company))) return false;
    
    // 날짜 필터 (다중 선택)
    if (filters.date.length > 0 && !filters.date.some(date => row.date?.includes(date))) return false;
    
    // 차량번호 필터 (단일 텍스트)
    if (filters.vehicleNumber && !(row.vehicleNumber || '').toString().includes(filters.vehicleNumber)) return false;
    
    // 운송구간 필터 (단일 텍스트)
    if (filters.route && !row.route?.includes(filters.route)) return false;
    
    // 품명 필터 (다중 선택)
    if (filters.itemName.length > 0) {
      const includeEmpty = filters.itemName.includes('품명 없음');
      const selectedNonEmpty = filters.itemName.filter(n => n !== '품명 없음');
      const itemNameVal = row.itemName || '';
      const isEmptyItemName = itemNameVal === null || itemNameVal === undefined || String(itemNameVal).trim() === '';
      const matchesNonEmpty = selectedNonEmpty.length > 0 && selectedNonEmpty.some(n => String(row.itemName || '').trim() === n);
      if (!((includeEmpty && isEmptyItemName) || matchesNonEmpty)) return false;
    }
    
    // 차종 필터 (다중 선택)
    if (filters.cargo.length > 0) {
      const includeEmpty = filters.cargo.includes('차종 없음');
      const selectedNonEmpty = filters.cargo.filter(c => c !== '차종 없음');
      const cargoVal = row.cargo || '';
      const isEmptyCargo = cargoVal === null || cargoVal === undefined || String(cargoVal).trim() === '';
      const matchesNonEmpty = selectedNonEmpty.length > 0 && selectedNonEmpty.some(c => row.cargo === c);
      if (!((includeEmpty && isEmptyCargo) || matchesNonEmpty)) return false;
    }
    
    // 소속 필터 (다중 선택)
    if (filters.department.length > 0) {
      const includeEmpty = filters.department.includes('소속 없음');
      const selectedNonEmpty = filters.department.filter(d => d !== '소속 없음');
      const departmentVal = row.department || '';
      const isEmptyDepartment = departmentVal === null || departmentVal === undefined || String(departmentVal).trim() === '';
      const matchesNonEmpty = selectedNonEmpty.length > 0 && selectedNonEmpty.some(d => row.department?.includes(d));
      if (!((includeEmpty && isEmptyDepartment) || matchesNonEmpty)) return false;
    }
    
    // 비고 필터 (단일 텍스트)
    if (filters.remarks && !row.remarks?.includes(filters.remarks)) return false;
    
    return true;
  });
  const safeFilteredRows = Array.isArray(filteredRows) ? filteredRows : [];
  const sortedRows = [...safeFilteredRows].sort((a, b) => {
    if (!a || !b) return 0;
    if (a.date < b.date) return -1;
    if (a.date > b.date) return 1;
    if ((a.company || '') < (b.company || '')) return -1;
    if ((a.company || '') > (b.company || '')) return 1;
    return (a.order ?? 0) - (b.order ?? 0);
  });
  const safeSortedRows = Array.isArray(sortedRows) ? sortedRows : [];
  const tableData = safeSortedRows.map(row => COLUMN_FIELDS.map(f => {
    if (!row) return '';
    if (f === 'billingAmount' || f === 'paymentAmount' || f === 'profit') {
      return formatAmountDisplay(row[f]);
    }
    return row[f] ?? '';
  }));

  // 합계 계산 (모든 타입/빈값/배열/NaN 방어, 문자열 내 모든 숫자 이외 문자 제거)
  // safeToNumber 함수를 공통 함수로 대체
  const billingSum = filteredRows.reduce((sum, r) => sum + safeAmountToNumber(r.billingAmount), 0);
  const paymentSum = filteredRows.reduce((sum, r) => sum + safeAmountToNumber(r.paymentAmount), 0);
  const dataCount = filteredRows.length; // 현재 로드된 데이터 건수

  // 합계 행 생성 (값만 넣음)
  const sumRow = [
    '', // 날짜
    '', // 거래처상호
    '', // 차량번호
    '', // 운송구간
    '', // 품명
    '', // 차종
    billingSum.toLocaleString(), // 청구운송비
    '', // 소속
    '', // 연락처
    paymentSum.toLocaleString(), // 지불운송비
    '', // 손익
    '', // 사업자번호
    '', // 이름
    '', // 거리
    '', // 중량
    '', // 비고
  ];
  const tableDataWithSum = [...tableData, sumRow];

  // 합계 행 readOnly 처리 (renderer 지정은 제거)
  const columnsWithSum = COLUMN_HEADERS.map((header, idx) => {
    return (idx === 0 || idx === 1) ? { readOnly: true } : {};
  });

  // 엑셀 다운로드
  const handleDownloadExcel = () => {
    const excelData = tableData.map(row => {
      const rowData = [...row];
      // 청구운송비(6), 지불운송비(9), 손익(10) 인덱스의 값을 숫자로 변환
      [6, 9, 10].forEach(idx => {
        if (rowData[idx]) {
          rowData[idx] = Number(String(rowData[idx]).replace(/[^\d.-]/g, '')) || 0;
        }
      });
      return rowData;
    });
    const ws = XLSX.utils.aoa_to_sheet([COLUMN_HEADERS, ...excelData]);
    
    // 엑셀용 열 너비 설정 (핸즈온테이블과 별도)
    const excelColWidths = [12, 15, 12, 20, 10, 8, 12, 10, 12, 12, 10, 15, 12, 8, 8, 15];
    ws['!cols'] = excelColWidths.map(wch => ({ wch }));
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '통합일보');
    XLSX.writeFile(wb, `통합일보_${dateRange.start}_${dateRange.end}.xlsx`);
  };

  // 날짜 입력창 값 변경 핸들러
  const handlePendingDateChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'start' | 'end') => {
    if (!e.target.value) return;
    setPendingDateRange(prev => ({ ...prev, [type]: e.target.value }));
  };

  // 조회 버튼 클릭 시
  const handleDateConfirm = () => {
    setDateRange(pendingDateRange);
    fetchRows(pendingDateRange.start, pendingDateRange.end);
  };

  // 필터 팝업 열기
  const openFilterPopup = (filterName: keyof typeof filterPopups) => {
    setFilterPopups(prev => ({ ...prev, [filterName]: true }));
    // 현재 필터 값을 임시 선택 상태로 복사
    setTempSelections(prev => ({ ...prev, [filterName]: [...filters[filterName]] }));
    // 검색어 초기화
    setFilterSearchTerms(prev => ({ ...prev, [filterName]: '' }));
  };

  // 필터 팝업 닫기
  const closeFilterPopup = (filterName: keyof typeof filterPopups) => {
    setFilterPopups(prev => ({ ...prev, [filterName]: false }));
    // 검색어 초기화
    setFilterSearchTerms(prev => ({ ...prev, [filterName]: '' }));
  };

  // 필터 적용 (확인 버튼 클릭 시)
  const applyFilter = (filterName: keyof typeof filterPopups) => {
    setFilters(prev => ({ ...prev, [filterName]: tempSelections[filterName] }));
    closeFilterPopup(filterName);
    
    // 연관 필터 초기화
    if (filterName === 'company') {
      setFilters(prev => ({ ...prev, itemName: [], cargo: [], department: [] }));
    } else if (filterName === 'itemName') {
      setFilters(prev => ({ ...prev, cargo: [], department: [] }));
    } else if (filterName === 'cargo') {
      setFilters(prev => ({ ...prev, department: [] }));
    }
  };

  // 필터 취소 (취소 버튼 클릭 시)
  const cancelFilter = (filterName: keyof typeof filterPopups) => {
    closeFilterPopup(filterName);
  };

  // 임시 선택 상태 변경
  const handleTempSelectionChange = (filterName: keyof typeof filterPopups, value: string) => {
    setTempSelections(prev => {
      const current = prev[filterName];
      if (current.includes(value)) {
        return { ...prev, [filterName]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [filterName]: [...current, value] };
      }
    });
  };

  // 전체 선택/해제
  const handleSelectAll = (filterName: keyof typeof filterPopups, allOptions: string[]) => {
    const current = tempSelections[filterName];
    if (current.length === allOptions.length) {
      setTempSelections(prev => ({ ...prev, [filterName]: [] }));
    } else {
      setTempSelections(prev => ({ ...prev, [filterName]: [...allOptions] }));
    }
  };

  // 필터 입력 핸들러 (텍스트 필터만 처리)
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // 텍스트 필터만 처리 (차량번호, 운송구간, 비고)
    if (name === 'vehicleNumber' || name === 'route' || name === 'remarks') {
      setFilters({ ...filters, [name]: value });
    }
  };

  // 운송구간 셀 클릭 핸들러
  const handleCellClick = async (row: number, col: number) => {
    if (col !== 3) return; // 3: 운송구간
    const rowData = sortedRows[row];
    if (!rowData) return;
    const { company, route, cargo } = rowData;
    const routeKey = makeRouteKey(company, route, cargo);
    setRouteHistoryKey(routeKey);
    setRouteHistoryOpen(true);
    setRouteHistoryLoading(true);
    setRouteHistoryOffset(20); // 최초 20건
    setRouteHistoryData({
      recentHistory: [],
      avg: null,
      mode: null,
      min: null,
      max: null,
      count: null,
      lastUsed: null,
    });
    try {
      const res = await fetch(`/api/route-history-cache/${routeKey}`);
      if (!res.ok) throw new Error('조회 실패');
      const data = await res.json();
      setRouteHistoryData(data);
    } catch (err) {
      setRouteHistoryData({
        recentHistory: [],
        avg: null,
        mode: null,
        min: null,
        max: null,
        count: null,
        lastUsed: null,
      });
    }
    setRouteHistoryLoading(false);
  };

  // 부분 업데이트 함수 추가 (타임스탬프 검증 포함)
  const updateSingleRow = useCallback((rowId: string, changedData: any, timestamp?: number) => {
    setAllRows(prev => {
      const newRows = [...prev];
      const rowIndex = newRows.findIndex(row => row.id === rowId);
      
      if (rowIndex !== -1) {
        const currentRow = newRows[rowIndex];
        
        // 타임스탬프 검증 (네트워크 지연 고려)
        if (timestamp && currentRow.updatedAt) {
          const serverTime = new Date(timestamp).getTime();
          const localTime = new Date(currentRow.updatedAt).getTime();
          
          // 5초 이상 차이나면 네트워크 지연으로 간주하고 업데이트 허용
          if (Math.abs(localTime - serverTime) < 5000 && localTime > serverTime) {
            console.log('로컬 데이터가 더 최신입니다. 업데이트 무시');
            return prev;
          }
        }
        
        // 변경된 필드만 업데이트
        newRows[rowIndex] = {
          ...currentRow,
          ...changedData,
          updatedAt: timestamp ? new Date(timestamp).toISOString() : currentRow.updatedAt
        };
      } else {
        // 행을 찾을 수 없으면 전체 재로드
        console.log('행을 찾을 수 없음. 전체 재로드 실행');
        debouncedFetch();
        return prev;
      }
      
      return newRows;
    });
  }, [debouncedFetch]);

  // 안전한 부분 업데이트 여부 확인
  const isSafeForPartialUpdate = useCallback((data: any) => {
    try {
      return (
        data.rowId && 
        data.changedData &&
        Object.keys(data.changedData).length > 0 &&
        (!data.operationType || data.operationType === 'cell_edit') &&
        // 추가 안전성 검증
        typeof data.rowId === 'string' &&
        typeof data.changedData === 'object' &&
        data.changedData !== null
      );
    } catch (error) {
      console.error('부분 업데이트 안전성 검증 오류:', error);
      return false;
    }
  }, []);

  // 소켓 이벤트 통합 및 날짜 비교 로직 추가
  useEffect(() => {
    const myUserId = auth.currentUser?.uid;
    const handleReportUpdated = (data: { 
      id?: string, 
      date: string, 
      eventId?: string, 
      userId?: string,
      rowId?: string,
      changedData?: any,
      operationType?: string,
      timestamp?: string
    }) => {
      // 배차일보에서 동일 사용자가 다른 탭/페이지에 입력해도 반영되도록 자기 이벤트도 수신
      if (!data.date || data.date < dateRange.start || data.date > dateRange.end) return;
      if (data.eventId && lastEventIdRef.current === data.eventId) return;
      lastEventIdRef.current = data.eventId || null;
      
      // 증분 처리: 행 추가/삭제는 별도 분기, 그 외는 부분 업데이트
      if (data.operationType === 'row_added' && data.rowId && data.changedData) {
        setAllRows(prev => {
          // 이미 있는 경우 중복 추가 방지
          if (prev.some(r => r.id === data.rowId)) return prev;
          const next = [...prev, { ...data.changedData }];
          // order 기준 정렬 유지 (있을 경우)
          try {
            return next.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
          } catch {
            return next;
          }
        });
        return;
      }
      if (data.operationType === 'row_deleted' && data.rowId) {
        setAllRows(prev => prev.filter(r => r.id !== data.rowId));
        return;
      }
      // 안전한 부분 업데이트 가능한 경우
      if (isSafeForPartialUpdate(data)) {
        updateSingleRow(data.rowId!, data.changedData!, data.timestamp ? new Date(data.timestamp).getTime() : undefined);
      } else {
        // 기존 동작 유지: 필요 시에만 전체 재조회
        setLoading(true);
        debouncedFetch();
      }
    };
    socket.on('report-updated', handleReportUpdated);
    // 레거시 호환(rowsUpdated)
    socket.on('rowsUpdated', handleReportUpdated as any);
    return () => {
      socket.off('report-updated', handleReportUpdated);
      socket.off('rowsUpdated', handleReportUpdated as any);
    };
  }, [dateRange, debouncedFetch, updateSingleRow, isSafeForPartialUpdate]);

  // allRows, filteredRows 상태 변화 추적
  useEffect(() => {
    // console.log('[상태] allRows', allRows);
  }, [allRows]);
  useEffect(() => {
    // console.log('[상태] filteredRows', filteredRows);
  }, [filteredRows]);

  // 셀 수정 핸들러
  const handleAfterChange = async (changes: any, source: string) => {
    if (!changes || source === 'loadData') return;
    for (const [row, col, oldValue, newValue] of changes) {
      // 합계 행은 무시
      if (row >= tableData.length) continue;
      const sortedRow = safeSortedRows[row];
      if (!sortedRow || !sortedRow.id || !sortedRow.date) continue;
      const field = COLUMN_FIELDS[col];
      if (!field) continue;
      let value = newValue;
      const rowId = sortedRow.id;
      
      // 청구운송비, 지급운송비 입력 시 profit 자동 계산
      let updateBody: any = { [field]: value };
      if (field === 'billingAmount' || field === 'paymentAmount') {
        const billingAmount = field === 'billingAmount' ? parseNumber(newValue) : parseNumber(sortedRow.billingAmount);
        const paymentAmount = field === 'paymentAmount' ? parseNumber(newValue) : parseNumber(sortedRow.paymentAmount);
        updateBody.profit = billingAmount - paymentAmount;
      }
      if (field === 'billingAmount' || field === 'paymentAmount' || field === 'profit') {
        value = newValue === '' ? '' : parseNumber(newValue);
      }
      setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'saving' } }));
      setAllRows(prev => prev.map(r => (r.id === rowId ? { ...r, [field]: value, ...(updateBody.profit !== undefined ? { profit: updateBody.profit } : {}) } : r)));
      try {
        const res = await fetch('/api/integrated-reports/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: sortedRow.id,
            date: sortedRow.date,
            ...updateBody,
            userId: auth.currentUser?.uid
          })
        });
        if (!res.ok) {
          if (res.status === 404) {
            const err = await res.json();
            alert(err.error || '해당 행이 존재하지 않습니다.');
            setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'error', message: err.error || '해당 행이 존재하지 않습니다.' } }));
            if (hotRef.current) {
              hotRef.current.hotInstance.setDataAtCell(row, col, oldValue);
            }
            continue;
          }
          setSaveStatus(prev => ({
            ...prev,
            [rowId]: { status: 'error', message: '저장 실패: 네트워크 또는 서버 오류' }
          }));
          if (hotRef.current) {
            hotRef.current.hotInstance.setDataAtCell(row, col, oldValue);
          }
        } else {
          setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'success' } }));
          setTimeout(() => {
            setSaveStatus(prev => {
              const newStatus = { ...prev };
              delete newStatus[rowId];
              return newStatus;
            });
          }, 2000);
        }
      } catch (error) {
        setSaveStatus(prev => ({
          ...prev,
          [rowId]: { status: 'error', message: '저장 실패: 네트워크 오류' }
        }));
        if (hotRef.current) {
          hotRef.current.hotInstance.setDataAtCell(row, col, oldValue);
        }
      }
    }
  };

  // cells 함수에서 하이라이트 적용
  const cells = (row: number, col: number) => {
    const realRow = safeSortedRows[row];
    if (!realRow) return {};
    const cellProps: any = {};
    const billingAmountColIdx = COLUMN_FIELDS.indexOf('billingAmount');
    const paymentAmountColIdx = COLUMN_FIELDS.indexOf('paymentAmount');

    // 청구운송비와 지불운송비 스타일 적용
    if (col === billingAmountColIdx) {
      cellProps.className = (cellProps.className || '') + ' htCenter htMiddle ellipsis-cell billing-amount';
    } else if (col === paymentAmountColIdx) {
      cellProps.className = (cellProps.className || '') + ' htCenter htMiddle ellipsis-cell payment-amount';
    } else {
      cellProps.className = (cellProps.className || '') + ' htCenter htMiddle ellipsis-cell';
    }

    return cellProps;
  };

  // 저장 상태별 스타일을 <style> 태그로 추가
  useEffect(() => {
    if (!document.getElementById('save-status-styles')) {
      const style = document.createElement('style');
      style.id = 'save-status-styles';
      style.innerHTML = `
        .saving-row, td.saving-row, .handsontable .saving-row { background: #fff3e0 !important; }
        .success-row, td.success-row, .handsontable .success-row { background: #e8f5e9 !important; }
        .error-row, td.error-row, .handsontable .error-row { background: #ffebee !important; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // 하이라이트 CSS 추가
  useEffect(() => {
    if (!document.getElementById('highlight-row-style')) {
      const style = document.createElement('style');
      style.id = 'highlight-row-style';
      style.innerHTML = `
        .highlight-row {
          background: #fffde7 !important;
          transition: background 0.7s;
          animation: highlight-fade 5s;
        }
        @keyframes highlight-fade {
          0% { background: #ffe082; }
          100% { background: #fffde7; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // order 중간값 계산 함수
  function getInsertOrder(date: string, baseOrder: number, direction: 'above' | 'below') {
    const dateRows = allRows.filter(r => r.date === date).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = dateRows.findIndex(r => r.order === baseOrder);
    if (idx === -1) return Date.now();
    if (direction === 'above') {
      const aboveOrder = idx > 0 ? dateRows[idx - 1].order : undefined;
      if (aboveOrder !== undefined) {
        return (aboveOrder + baseOrder) / 2;
      } else {
        return baseOrder - 1000;
      }
    } else {
      const belowOrder = idx < dateRows.length - 1 ? dateRows[idx + 1].order : undefined;
      if (belowOrder !== undefined) {
        return (baseOrder + belowOrder) / 2;
      } else {
        return baseOrder + 1000;
      }
    }
  }

  // 행 추가 (API)
  const handleAddRow = async (date: string, baseOrder: number, direction: 'above' | 'below', company: string) => {
    const order = getInsertOrder(date, baseOrder, direction);
    const now = Date.now();
    const rowId = `${date}_${now}_${Math.floor(Math.random()*10000)}`;
    const newRowData = {
      id: rowId,
      rowId,
      order,
      date,
      company,
      vehicleNumber: '',
      route: '',
      itemName: '',
      cargo: '',
      billingAmount: 0,
      department: '',
      contact: '',
      paymentAmount: 0,
      businessNumber: '',
      driverName: '',
      distance: 0,
      weight: 0,
      profit: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    // console.log('[행추가] handleAddRow', newRowData);
    const res = await fetch(`/api/dispatch-reports/${date}/rows`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newRowData)
    });
    // console.log('[행추가] fetch 응답', res.status);
    // 전체 fetchRows로 갱신
    setLoading(true);
    const fetchRows = async () => {
      // console.log('[행추가] fetchRows 실행');
      const res = await fetch(`/api/integrated-reports?start=${dateRange.start}&end=${dateRange.end}`);
      const rows = await res.json();
      // console.log('[행추가] fetchRows rows', rows);
      setAllRows(rows);
      setLoading(false);
    };
    fetchRows();
  };

  // 행 삭제 (API)
  const handleDeleteRow = async (rowId: string, date: string) => {
    await fetch(`/api/dispatch-reports/${date}/rows/${rowId}`, {
      method: 'DELETE'
    });
    // 전체 fetchRows로 갱신
    setLoading(true);
    const fetchRows = async () => {
      const res = await fetch(`/api/integrated-reports?start=${dateRange.start}&end=${dateRange.end}`);
      const rows = await res.json();
      setAllRows(rows);
      setLoading(false);
    };
    fetchRows();
  };

  // 거래처명 비교를 위한 정규화 함수
  function normalizeCompanyName(name: string) {
    if (!name) return '';
    return name
      .replace(/\(.*?\)/g, '') // 괄호 및 괄호 안 내용 제거
      .replace(/(주식회사|유한회사|유한|주|유|합|재|사)/gi, '') // 법인 구분어 제거
      .replace(/\s+/g, '') // 모든 공백 제거
      .toLowerCase()
      .trim();
  }

  // 금액분배 버튼 클릭
  const handleDistributeClick = () => {
    setShowDistributeModal(true);
    setDistributeAmount('');
  };

  // 금액 분배 및 저장
  const handleDistributeConfirm = async () => {
    const total = parseNumber(distributeAmount);
    if (!total || filteredRows.length === 0) return;
    setDistributeLoading(true);
    setLoading(true);
    const base = Math.floor(total / filteredRows.length);
    const remain = total - base * filteredRows.length;
    for (let i = 0; i < filteredRows.length; i++) {
      const row = filteredRows[i];
      const value = i === filteredRows.length - 1 ? base + remain : base;
      // 셀에 값 입력 및 저장
      await handleAfterChange(
        [[i, COLUMN_FIELDS.indexOf('billingAmount'), row.billingAmount, value]],
        'edit'
      );
    }
    setDistributeLoading(false);
    setLoading(false);
    setShowDistributeModal(false);
  };

  const handleRouteHistoryLoadMore = async () => {
    if (!routeHistoryKey) return;
    setRouteHistoryLoading(true);
    try {
      const lastRow = routeHistoryData.recentHistory[routeHistoryData.recentHistory.length - 1];
      const startAfter = lastRow ? `${lastRow.date}|${lastRow.id}` : undefined;
      const url = `/api/route-history-rows/${routeHistoryKey}?limit=20${startAfter ? `&startAfter=${encodeURIComponent(startAfter)}` : ''}`;
      const res = await fetch(url);
      const moreRows = await res.json();
      if (!Array.isArray(moreRows)) {
        setRouteHistoryLoading(false);
        return;
      }
      setRouteHistoryData(prev => ({
        ...prev,
        recentHistory: [...(prev.recentHistory || []), ...moreRows]
      }));
    } catch (err) {
      // 에러 처리
    }
    setRouteHistoryLoading(false);
  };

  // 차량별+노선별 routeKey 생성 함수
  function makeVehicleRouteKey(vehicleNumber: string, company: string, route: string, cargo: string) {
    return safeFirestoreId(`${vehicleNumber}|${company}|${route}|${cargo}`);
  }


  // 차량번호 셀 클릭 핸들러
  const handleVehicleCellClick = async (row: number, col: number) => {
    if (col !== 2) return; // 2: 차량번호
    const rowData = sortedRows[row];
    if (!rowData) return;
    const { vehicleNumber, company, route, cargo } = rowData;
    // 캐시 키를 생성할 때 전체 차량번호를 사용하도록 수정
    const routeKey = makeVehicleRouteKey(vehicleNumber, company, route, cargo);
    setVehicleHistoryKey(routeKey);
    setVehicleHistoryVehicleNumber(vehicleNumber);
    setVehicleHistoryOpen(true);
    setVehicleHistoryLoading(true);
    setVehicleHistoryData({
      recentHistory: [],
      avg: null,
      mode: null,
      min: null,
      max: null,
      count: null,
      lastUsed: null,
    });
    try {
      const res = await fetch(`/api/route-history-cache/${routeKey}`);
      if (res.ok) {
        const data = await res.json();
        setVehicleHistoryData(data);
      } else {
        setVehicleHistoryData({
          recentHistory: [],
          avg: null,
          mode: null,
          min: null,
          max: null,
          count: null,
          lastUsed: null,
        });
      }
    } catch (err) {
      setVehicleHistoryData({
        recentHistory: [],
        avg: null,
        mode: null,
        min: null,
        max: null,
        count: null,
        lastUsed: null,
      });
    }
    setVehicleHistoryLoading(false);
  };

  // 차량 이력 더보기
  const handleVehicleHistoryLoadMore = async () => {
    if (!vehicleHistoryKey) return;
    setVehicleHistoryLoading(true);
    try {
      const lastRow = vehicleHistoryData.recentHistory[vehicleHistoryData.recentHistory.length - 1];
      // console.log('[프론트] lastRow:', lastRow);
      const startAfter = lastRow ? `${lastRow.date}|${lastRow.id}` : undefined;
      const url = `/api/route-history-rows/${vehicleHistoryKey}?limit=20${startAfter ? `&startAfter=${encodeURIComponent(startAfter)}` : ''}`;
      // console.log('[프론트] fetch url:', url);
      const res = await fetch(url);
      const moreRows = await res.json();
      // console.log('[프론트] 응답 row 수:', moreRows.length, (moreRows as any[]).map((r: any) => ({date: r.date, id: r.id})));
      if (!Array.isArray(moreRows)) {
        setVehicleHistoryLoading(false);
        return;
      }
      setVehicleHistoryData((prev: any) => ({
        ...prev,
        recentHistory: [...(prev.recentHistory || []), ...moreRows]
      }));
    } catch (err) {
      console.error('[프론트] 더보기 에러:', err);
    }
    setVehicleHistoryLoading(false);
  };

  // 필터 옵션 가져오기 함수
  const getAllOptionsForFilter = (filterName: string): string[] => {
    switch (filterName) {
      case 'company':
        return companyOptions;
      case 'date':
        return dateOptions;
      case 'itemName':
        return itemNameOptions;
      case 'cargo':
        return cargoOptions;
      case 'department':
        return departmentOptions;
      default:
        return [];
    }
  };

  // 검색어로 필터링된 옵션 가져오기
  const getFilteredOptions = (filterName: keyof typeof filterSearchTerms): string[] => {
    const allOptions = getAllOptionsForFilter(filterName);
    const searchTerm = filterSearchTerms[filterName].toLowerCase().trim();
    
    if (!searchTerm) return allOptions;
    
    return allOptions.filter(option => 
      option.toLowerCase().includes(searchTerm)
    );
  };

  // 동적 필터 옵션 생성 함수들
  const getDynamicItemNameOptions = () => {
    let filteredRows = sortedRows; // allRows 대신 sortedRows 사용
    if (filters.company.length > 0) {
      filteredRows = filteredRows.filter(row => filters.company.some(company => row.company?.includes(company)));
    }
    const itemNames = filteredRows.map(row => row.itemName || '').filter(itemName => itemName !== '' && itemName.trim() !== '');
    const uniqueItemNames = Array.from(new Set(itemNames));
    const hasEmptyItemName = filteredRows.some(row => {
      const itemName = row.itemName;
      return !itemName || itemName === '' || itemName === null || itemName === undefined || itemName.trim() === '';
    });
    return hasEmptyItemName ? [...uniqueItemNames, '품명 없음'] : uniqueItemNames;
  };

  const getDynamicCargoOptions = () => {
    let filteredRows = sortedRows; // allRows 대신 sortedRows 사용
    if (filters.company.length > 0) {
      filteredRows = filteredRows.filter(row => filters.company.some(company => row.company?.includes(company)));
    }
    if (filters.itemName.length > 0) {
      filteredRows = filteredRows.filter(row => {
        if (filters.itemName.includes('품명 없음')) {
          const itemName = row.itemName;
          return !itemName || itemName === '' || itemName === null || itemName === undefined || itemName.trim() === '';
        }
        return filters.itemName.some(selectedName => String(row.itemName ?? '').trim() === selectedName);
      });
    }
    const cargoTypes = filteredRows.map(row => row.cargo || '').filter(cargo => cargo !== '' && cargo.trim() !== '');
    const uniqueCargoTypes = Array.from(new Set(cargoTypes));
    const hasEmptyCargo = filteredRows.some(row => {
      const cargo = row.cargo;
      return !cargo || cargo === '' || cargo === null || cargo === undefined || cargo.trim() === '';
    });
    return hasEmptyCargo ? [...uniqueCargoTypes, '차종 없음'] : uniqueCargoTypes;
  };

  const getDynamicDepartmentOptions = () => {
    let filteredRows = sortedRows; // allRows 대신 sortedRows 사용
    if (filters.company.length > 0) {
      filteredRows = filteredRows.filter(row => filters.company.some(company => row.company?.includes(company)));
    }
    if (filters.itemName.length > 0) {
      filteredRows = filteredRows.filter(row => {
        if (filters.itemName.includes('품명 없음')) {
          const itemName = row.itemName;
          return !itemName || itemName === '' || itemName === null || itemName === undefined || itemName.trim() === '';
        }
        return filters.itemName.some(selectedName => String(row.itemName ?? '').trim() === selectedName);
      });
    }
    if (filters.cargo.length > 0) {
      filteredRows = filteredRows.filter(row => {
        if (filters.cargo.includes('차종 없음')) {
          const cargo = row.cargo;
          return !cargo || cargo === '' || cargo === null || cargo === undefined || cargo.trim() === '';
        }
        return filters.cargo.some(cargo => row.cargo === cargo);
      });
    }
    const departments = filteredRows.map(row => row.department || '').filter(dept => dept !== '' && dept.trim() !== '');
    const uniqueDepartments = Array.from(new Set(departments));
    const hasEmptyDepartment = filteredRows.some(row => {
      const department = row.department;
      return !department || department === '' || department === null || department === undefined || department.trim() === '';
    });
    return hasEmptyDepartment ? [...uniqueDepartments, '소속 없음'] : uniqueDepartments;
  };

  // 동적 필터 옵션 적용
  const companyOptions = Array.from(new Set(sortedRows.map(row => row.company).filter(Boolean)));
  const dateOptions = Array.from(new Set(sortedRows.map(row => row.date).filter(Boolean)));
  const itemNameOptions = getDynamicItemNameOptions();
  const cargoOptions = getDynamicCargoOptions();
  const departmentOptions = getDynamicDepartmentOptions();

  // 필터 초기화 핸들러 추가
  const handleResetFilters = () => {
    setFilters({
      company: [],
      date: [],
      vehicleNumber: '',
      route: '',
      itemName: [],
      cargo: [],
      department: [],
      remarks: ''
    });
  };

  // 비밀번호 확인 핸들러
  const handlePasswordConfirm = () => {
    if (password === '3500') {
      setEditMode('edit');
      setShowPasswordModal(false);
      setPassword('');
      setPasswordError('');
    } else {
      setPasswordError('비밀번호가 올바르지 않습니다.');
    }
  };

  // 비밀번호 모달 닫기 핸들러
  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
    setPassword('');
    setPasswordError('');
  };

  // ESC 키로 사이드바 및 모달 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setRouteHistoryOpen(false);
        setVehicleHistoryOpen(false);
        if (showPasswordModal) {
          handlePasswordCancel();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPasswordModal]); // showPasswordModal 의존성 추가

  // 통합일보 전용 스타일 주입 (다른 페이지와 style id 충돌 방지)
  useEffect(() => {
    const STYLE_ID = 'integrated-report-cell-style';
    let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.innerHTML = `
      .ellipsis-cell {
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        max-width: 120px !important;
      }
      .billing-amount {
        background-color: #fff6ef !important;
        color: #d32f2f !important;
        font-weight: 700 !important;
      }
      .payment-amount {
        background-color: #edf2ff !important;
        color: #1976d2 !important;
        font-weight: 700 !important;
      }
    `;
  }, []);

  return (
    <div style={{ width: '100%', maxWidth: '100%', height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* 상단 UI 업그레이드 - 2단 구조 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
        {/* 1단: 엑셀/금액분배 버튼 + 날짜피커 + 전체화면 버튼 */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 16,
          position: 'relative'
        }}>
          {/* 왼쪽: 엑셀/금액분배 버튼 */}
          <div style={{ display: 'flex', flexDirection: 'row', gap: 0 }}>
            <button
              onClick={handleDownloadExcel}
              style={{
                background: '#fff',
                color: '#222',
                border: '1px solid #d1d5db',
                borderRight: 'none',
                borderRadius: '4px 0 0 4px',
                padding: '0 22px',
                fontWeight: 500,
                fontSize: '15px',
                height: 36,
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: 'none',
                outline: 'none',
                transition: 'background 0.2s',
                margin: 0
              }}
              onMouseOver={e => e.currentTarget.style.background = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              엑셀
            </button>
            <button
              onClick={handleDistributeClick}
              style={{
                background: '#fff',
                color: '#222',
                border: '1px solid #d1d5db',
                borderRadius: '0 4px 4px 0',
                padding: '0 22px',
                fontWeight: 500,
                fontSize: '15px',
                height: 36,
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: 'none',
                outline: 'none',
                transition: 'background 0.2s',
                margin: 0
              }}
              onMouseOver={e => e.currentTarget.style.background = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              금액분배
            </button>
          </div>

          {/* 중앙: 날짜피커 */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 8,
            padding: '8px 16px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)'
          }}>
            <CalendarTodayIcon sx={{ fontSize: 16, color: '#6b7280' }} />
            <input 
              type="date" 
              value={pendingDateRange.start} 
              onChange={e => handlePendingDateChange(e, 'start')}
              style={{
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                fontSize: 13,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                width: 130
              }}
            />
            <span style={{ color: '#6b7280', fontWeight: 500, fontSize: 13 }}>~</span>
            <input 
              type="date" 
              value={pendingDateRange.end} 
              onChange={e => handlePendingDateChange(e, 'end')}
              style={{
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                fontSize: 13,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                width: 130
              }}
            />
            <button 
              onClick={handleDateConfirm} 
              style={{ 
                padding: '6px 12px', 
                borderRadius: 4, 
                border: '1px solid #1976d2', 
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', 
                color: '#fff', 
                fontWeight: 600, 
                cursor: 'pointer', 
                fontSize: 13,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                transition: 'all 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              조회
            </button>
          </div>

          {/* 오른쪽: 전체화면 버튼 */}
          <div style={{ marginLeft: 'auto' }}>
            <FullScreenButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
          </div>
        </div>

        {/* 2단: 필터들 (카드 형태로 세련되게) + 편집모드 라디오버튼 */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: 12,
          padding: '20px',
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          {/* 거래처 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <BusinessIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              거래처
            </label>
            <button
              onClick={() => openFilterPopup('company')}
              style={{
                width: 140,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.company.length === 0 
                  ? '전체' 
                  : filters.company.length === 1 
                    ? filters.company[0] 
                    : `${filters.company.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 날짜 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              날짜
            </label>
            <button
              onClick={() => openFilterPopup('date')}
              style={{
                width: 110,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.date.length === 0 
                  ? '전체' 
                  : filters.date.length === 1 
                    ? filters.date[0] 
                    : `${filters.date.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 차량번호 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <DirectionsCarIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              차량번호
            </label>
            <input 
              name="vehicleNumber" 
              value={filters.vehicleNumber} 
              onChange={handleFilterChange} 
              placeholder="차량번호 입력"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              style={{ 
                width: 130, 
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            />
          </div>

          {/* 운송구간 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <RouteIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              운송구간
            </label>
            <input 
              name="route" 
              value={filters.route} 
              onChange={handleFilterChange} 
              placeholder="운송구간 입력"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              style={{ 
                width: 140, 
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            />
          </div>

          {/* 품명 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <InventoryIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              품명
            </label>
            <button
              onClick={() => openFilterPopup('itemName')}
              style={{
                width: 120,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.itemName.length === 0 
                  ? '전체' 
                  : filters.itemName.length === 1 
                    ? filters.itemName[0] 
                    : `${filters.itemName.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 차종 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <LocalShippingIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              차종
            </label>
            <button
              onClick={() => openFilterPopup('cargo')}
              style={{
                width: 100,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.cargo.length === 0 
                  ? '전체' 
                  : filters.cargo.length === 1 
                    ? filters.cargo[0] 
                    : `${filters.cargo.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 소속 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <GroupsIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              소속
            </label>
            <button
              onClick={() => openFilterPopup('department')}
              style={{
                width: 120,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.department.length === 0 
                  ? '전체' 
                  : filters.department.length === 1 
                    ? filters.department[0] 
                    : `${filters.department.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 비고 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <NoteIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              비고
            </label>
            <input 
              name="remarks" 
              value={filters.remarks} 
              onChange={handleFilterChange} 
              placeholder="비고 검색"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              style={{ 
                width: 130, 
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            />
          </div>

          {/* 필터 초기화 버튼 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'flex-end' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', opacity: 0 }}>⠀</label>
            <button
              onClick={handleResetFilters}
              title="필터 초기화"
              style={{ 
                width: 40, 
                height: 36, 
                fontSize: 16, 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                padding: 0,
                border: '1px solid #d1d5db',
                borderRadius: 6,
                background: '#fff',
                color: '#6b7280',
                transition: 'all 0.2s'
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              ↺
            </button>
          </div>

          {/* 편집모드 라디오버튼 - 오른쪽 끝에 배치 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'flex-end', marginLeft: 'auto' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <EditIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              편집모드
            </label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label style={{ fontSize: 14, color: editMode === 'edit' ? '#1976d2' : '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <input 
                  type="radio" 
                  name="editMode" 
                  value="edit" 
                  checked={editMode === 'edit'} 
                  onChange={() => {
                    setShowPasswordModal(true);
                    setPassword('');
                    setPasswordError('');
                  }} 
                  style={{ margin: 0 }}
                />
                편집모드
              </label>
              <label style={{ fontSize: 14, color: editMode === 'fare' ? '#1976d2' : '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <input 
                  type="radio" 
                  name="editMode" 
                  value="fare" 
                  checked={editMode === 'fare'} 
                  onChange={() => setEditMode('fare')} 
                  style={{ margin: 0 }}
                />
                운임입력모드
              </label>
            </div>
          </div>
        </div>
      </div>
      {/* 합계 표시 영역 - 정산일보와 동일한 스타일 */}
      <div style={{ width: '100%', position: 'relative', height: 18, margin: '1px 0 1px 0', top: '-8px' }}>
        <div style={{
          position: 'absolute',
          left: 'calc(90px + 150px + 120px + 190px + 90px + 60px + 45px)', // 청구운송비 컬럼 중앙
          transform: 'translateX(-50%)',
          fontWeight: 700,
          fontSize: 15,
        }}>
          청구운송비합계 : <span style={{ color: '#d32f2f' }}>{billingSum.toLocaleString()}</span>원
        </div>
        <div style={{
          position: 'absolute',
          left: 'calc(90px + 150px + 120px + 190px + 90px + 60px + 90px + 100px + 120px + 45px)', // 지불운송비 컬럼 중앙
          transform: 'translateX(-50%)',
          fontWeight: 700,
          fontSize: 15,
        }}>
          지불운송비합계 : <span style={{ color: '#1976d2' }}>{paymentSum.toLocaleString()}</span>원
        </div>
        <div style={{
          position: 'absolute',
          left: 'calc(90px + 150px + 120px + 190px + 90px + 60px + 90px + 100px + 120px + 90px + 90px + 60px)', // 사업자번호 컬럼 중앙
          transform: 'translateX(-50%)',
          fontWeight: 700,
          fontSize: 15,
        }}>
          건수 : {dataCount}건
        </div>
      </div>
      
      {/* 표 */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <HotTable
          ref={hotRef}
          data={tableData}
          colHeaders={COLUMN_HEADERS}
          rowHeaders={true}
          width="100%"
          height="100%"
          colWidths={colWidths}
          manualRowResize={true}
          manualColumnResize={true}
          autoColumnSize={false}
          autoRowSize={false}
          className="htCenter"
          licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
          afterChange={handleAfterChange}
          afterCreateRow={() => {}}
          afterRemoveRow={() => {}}
          contextMenu={false}
          readOnly={false}
          columns={COLUMN_HEADERS.map((header, idx) => {
            if (editMode === 'fare') {
              // 운임입력모드: 청구/지불만 편집 가능
              if (header === '청구운송비') return { readOnly: false, renderer: 'RedMoneyRenderer' };
              if (header === '지불운송비') return { readOnly: false, renderer: 'BlueMoneyRenderer' };
              return { readOnly: true };
            } else {
              // 편집모드: 날짜/거래처상호만 readOnly, 나머지 편집 가능
              if (header === '날짜' || header === '거래처상호') return { readOnly: true };
              if (header === '청구운송비') return { readOnly: false, renderer: 'RedMoneyRenderer' };
              if (header === '지불운송비') return { readOnly: false, renderer: 'BlueMoneyRenderer' };
              return { readOnly: false };
            }
          })}
          afterOnCellMouseDown={(e, coords) => {
            if (editMode === 'fare') {
              // 운임입력모드: 통계 사이드바 동작 유지
              if (coords.col === 3 && coords.row >= 0) handleCellClick(coords.row, coords.col);
              if (coords.col === 2 && coords.row >= 0) handleVehicleCellClick(coords.row, coords.col);
            }
            // 편집모드일 때는 아무 동작 없음
          }}
          cells={cells}
        />
      </div>
      
      <RouteHistorySidebar
        open={routeHistoryOpen}
        onClose={() => setRouteHistoryOpen(false)}
        data={routeHistoryData.recentHistory || []}
        avg={routeHistoryData.avg}
        mode={routeHistoryData.mode}
        min={routeHistoryData.min}
        max={routeHistoryData.max}
        count={routeHistoryData.count}
        lastUsed={routeHistoryData.lastUsed}
        onLoadMore={handleRouteHistoryLoadMore}
        loading={routeHistoryLoading}
      />

      <VehicleHistorySidebar
        open={vehicleHistoryOpen}
        onClose={() => setVehicleHistoryOpen(false)}
        data={vehicleHistoryData.recentHistory || []}
        avg={vehicleHistoryData.avg}
        mode={vehicleHistoryData.mode}
        min={vehicleHistoryData.min}
        max={vehicleHistoryData.max}
        count={vehicleHistoryData.count}
        lastUsed={vehicleHistoryData.lastUsed}
        onLoadMore={handleVehicleHistoryLoadMore}
        loading={vehicleHistoryLoading}
        vehicleNumber={vehicleHistoryVehicleNumber}
      />

      {/* 저장 실패 시 우측 하단에 에러 메시지 토스트 표시 */}
      {Object.entries(saveStatus).map(([rowId, status]) => (
        status.status === 'error' && (
          <div
            key={rowId}
            style={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              background: '#d32f2f',
              color: 'white',
              padding: '12px 24px',
              borderRadius: 4,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              zIndex: 1000,
              animation: 'fadeIn 0.3s ease-in-out'
            }}
          >
            {status.message}
          </div>
        )
      ))}

      {/* 금액분배 모달 */}
      {showDistributeModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
          <div style={{ background: '#fff', padding: 32, borderRadius: 8, minWidth: 320 }}>
            <div style={{ marginBottom: 16, fontWeight: 600 }}>총 금액을 입력하세요</div>
            <input
              type="number"
              value={distributeAmount}
              onChange={e => setDistributeAmount(e.target.value)}
              style={{ width: '100%', fontSize: 18, padding: 8, marginBottom: 16 }}
              placeholder="예: 50000000"
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleDistributeConfirm} style={{ flex: 1, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 5, padding: '10px 0', fontWeight: 600 }}>분배</button>
              <button onClick={() => setShowDistributeModal(false)} style={{ flex: 1, background: '#eee', color: '#333', border: 'none', borderRadius: 5, padding: '10px 0', fontWeight: 600 }}>취소</button>
            </div>
          </div>
        </div>
      )}

      {/* 필터 팝업들 */}
      {filterPopups.company && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 300,
            maxHeight: '80vh',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>거래처 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.company}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, company: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.company.length === getFilteredOptions('company').length && getFilteredOptions('company').length > 0}
                onChange={() => handleSelectAll('company', getFilteredOptions('company'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 - 스크롤 영역 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('company').map(company => (
                <label key={company} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.company.includes(company)}
                    onChange={() => handleTempSelectionChange('company', company)}
                    style={{ margin: 0 }}
                  />
                  <span>{company}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('company')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('company')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 날짜 필터 팝업 */}
      {filterPopups.date && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 300,
            maxHeight: '80vh',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>날짜 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.date}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, date: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.date.length === getFilteredOptions('date').length && getFilteredOptions('date').length > 0}
                onChange={() => handleSelectAll('date', getFilteredOptions('date'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 - 스크롤 영역 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('date').map(date => (
                <label key={date} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.date.includes(date)}
                    onChange={() => handleTempSelectionChange('date', date)}
                    style={{ margin: 0 }}
                  />
                  <span>{date}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('date')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('date')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 품명 필터 팝업 */}
      {filterPopups.itemName && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 300,
            maxHeight: '80vh',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>품명 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.itemName}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, itemName: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.itemName.length === getFilteredOptions('itemName').length && getFilteredOptions('itemName').length > 0}
                onChange={() => handleSelectAll('itemName', getFilteredOptions('itemName'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 - 스크롤 영역 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('itemName').map(itemName => (
                <label key={itemName} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.itemName.includes(itemName)}
                    onChange={() => handleTempSelectionChange('itemName', itemName)}
                    style={{ margin: 0 }}
                  />
                  <span>{itemName}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('itemName')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('itemName')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 차종 필터 팝업 */}
      {filterPopups.cargo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 300,
            maxHeight: '80vh',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>차종 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.cargo}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, cargo: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.cargo.length === getFilteredOptions('cargo').length && getFilteredOptions('cargo').length > 0}
                onChange={() => handleSelectAll('cargo', getFilteredOptions('cargo'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 - 스크롤 영역 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('cargo').map(cargo => (
                <label key={cargo} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.cargo.includes(cargo)}
                    onChange={() => handleTempSelectionChange('cargo', cargo)}
                    style={{ margin: 0 }}
                  />
                  <span>{cargo}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('cargo')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('cargo')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 소속 필터 팝업 */}
      {filterPopups.department && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 300,
            maxHeight: '80vh',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>소속 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.department}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, department: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.department.length === getFilteredOptions('department').length && getFilteredOptions('department').length > 0}
                onChange={() => handleSelectAll('department', getFilteredOptions('department'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 - 스크롤 영역 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('department').map(department => (
                <label key={department} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.department.includes(department)}
                    onChange={() => handleTempSelectionChange('department', department)}
                    style={{ margin: 0 }}
                  />
                  <span>{department}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('department')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('department')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 편집모드 비밀번호 모달 */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
          <div style={{ 
            background: '#fff', 
            padding: 32, 
            borderRadius: 12, 
            width: 400, 
            maxWidth: '90vw',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            boxSizing: 'border-box'
          }}>
            <div style={{ marginBottom: 8, fontWeight: 700, fontSize: 18, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
              <EditIcon sx={{ fontSize: 20, color: '#1976d2' }} />
              편집모드 인증
            </div>
            <div style={{ marginBottom: 20, fontSize: 14, color: '#6b7280' }}>
              편집모드를 사용하려면 비밀번호를 입력하세요
            </div>
            <input
              type="password"
              name="edit-mode-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handlePasswordConfirm()}
              onFocus={(e) => {
                // 포커스 시 잠깐 readOnly를 해제하여 자동완성 차단
                e.target.readOnly = false;
                // 추가로 자동완성 완전 차단
                e.target.setAttribute('autocomplete', 'new-password');
                e.target.setAttribute('data-form-type', 'other');
              }}
              onBlur={(e) => {
                // 포커스 아웃 시 다시 readOnly로 설정
                e.target.readOnly = true;
              }}
              style={{ 
                width: '100%', 
                fontSize: 16, 
                padding: '12px 16px', 
                marginBottom: 8,
                border: passwordError ? '1px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: 8,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              placeholder="비밀번호 입력"
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
              readOnly
              autoFocus
            />
            {passwordError && (
              <div style={{ marginBottom: 16, fontSize: 13, color: '#ef4444' }}>
                {passwordError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button 
                onClick={handlePasswordConfirm} 
                style={{ 
                  flex: 1, 
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 8, 
                  padding: '12px 0', 
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                확인
              </button>
              <button 
                onClick={handlePasswordCancel} 
                style={{ 
                  flex: 1, 
                  background: '#f3f4f6', 
                  color: '#374151', 
                  border: '1px solid #d1d5db', 
                  borderRadius: 8, 
                  padding: '12px 0', 
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#e5e7eb'}
                onMouseOut={e => e.currentTarget.style.background = '#f3f4f6'}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 로딩 오버레이: loading 또는 distributeLoading 중 하나라도 true면 표시 */}
      {(loading || distributeLoading) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            padding: 40,
            borderRadius: 16,
            boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontSize: 22,
            fontWeight: 700,
            color: '#1976d2'
          }}>
            <div className="spinner" style={{ marginBottom: 18 }}>
              <svg width="48" height="48" viewBox="0 0 50 50">
                <circle cx="25" cy="25" r="20" fill="none" stroke="#1976d2" strokeWidth="6" strokeDasharray="31.415, 31.415" transform="rotate(72.5042 25 25)">
                  <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>
            로딩 중...
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegratedReportPage;